import type { SerializedChatMessage } from "@/lib/pendingThreadStore";

export type PersistPhase = "early" | "final";

export type PersistMismatchReason = "count" | "id" | "phase" | "not_array";

export type PersistCheckResult = {
  idsMatch: boolean;
  phaseOk: boolean;
  reason?: PersistMismatchReason;
};

function messageContentKey(content: unknown): string {
  if (typeof content === "string") return content;
  try {
    return JSON.stringify(content);
  } catch {
    return String(content);
  }
}

function dbMessageId(dbMessages: unknown, index: number): string | null {
  if (!Array.isArray(dbMessages)) return null;
  const row = dbMessages[index] as { id?: string } | undefined;
  return row?.id != null ? String(row.id) : null;
}

/** Compare local draft messages with DB row (all ids in order). */
export function messagesMatchDb(
  localMessages: SerializedChatMessage[],
  dbMessages: unknown
): boolean {
  if (!Array.isArray(dbMessages)) return localMessages.length === 0;
  if (dbMessages.length !== localMessages.length) return false;
  return localMessages.every((m, i) => {
    const dbId = dbMessageId(dbMessages, i);
    return dbId != null && String(m.id) === dbId;
  });
}

/** True when the last message is a terminal assistant turn. */
export function turnIsComplete(messages: SerializedChatMessage[]): boolean {
  if (messages.length === 0) return false;
  const last = messages[messages.length - 1];
  if (last.role !== "assistant") return false;
  const status = last.status ?? "complete";
  return status === "complete" || status === "partial" || status === "error";
}

/** Choose persist phase from local message completeness. */
export function resolvePersistPhase(messages: SerializedChatMessage[]): PersistPhase {
  return turnIsComplete(messages) ? "final" : "early";
}

/**
 * Evaluate id-order match and phase gate separately so callers can treat
 * "IDs saved, turn incomplete" as synced_partial instead of a hard failure.
 */
export function evaluatePersistMatch(
  localMessages: SerializedChatMessage[],
  dbMessages: unknown,
  phase: PersistPhase
): PersistCheckResult {
  if (!Array.isArray(dbMessages)) {
    if (localMessages.length === 0) {
      const phaseOk =
        phase === "early" ? false : turnIsComplete(localMessages);
      return { idsMatch: true, phaseOk, reason: phaseOk ? undefined : "phase" };
    }
    return { idsMatch: false, phaseOk: false, reason: "not_array" };
  }

  if (dbMessages.length !== localMessages.length) {
    return { idsMatch: false, phaseOk: false, reason: "count" };
  }

  const idsMatch = localMessages.every((m, i) => {
    const dbId = dbMessageId(dbMessages, i);
    return dbId != null && String(m.id) === dbId;
  });
  if (!idsMatch) {
    return { idsMatch: false, phaseOk: false, reason: "id" };
  }

  if (phase === "early") {
    const last = localMessages[localMessages.length - 1];
    const phaseOk = last?.role === "user";
    return { idsMatch: true, phaseOk, reason: phaseOk ? undefined : "phase" };
  }

  const phaseOk = turnIsComplete(localMessages);
  return { idsMatch: true, phaseOk, reason: phaseOk ? undefined : "phase" };
}

/** Phase-specific verification rules on top of full id-order match. */
export function messagesMatchDbForPhase(
  localMessages: SerializedChatMessage[],
  dbMessages: unknown,
  phase: PersistPhase
): boolean {
  const check = evaluatePersistMatch(localMessages, dbMessages, phase);
  return check.idsMatch && check.phaseOk;
}

/** Lightweight fingerprint for logging / debugging mismatches. */
export function fingerprintMessages(messages: SerializedChatMessage[]): string {
  if (messages.length === 0) return "empty";
  const last = messages[messages.length - 1];
  return `${messages.length}:${last.id}:${messageContentKey(last.content).length}`;
}

/** Human-readable mismatch detail for banners / logs. */
export function describePersistMismatch(
  localMessages: SerializedChatMessage[],
  dbMessages: unknown,
  phase: PersistPhase,
  check?: PersistCheckResult
): string {
  const result = check ?? evaluatePersistMatch(localMessages, dbMessages, phase);
  if (result.idsMatch && result.phaseOk) return "";

  const dbCount = Array.isArray(dbMessages) ? dbMessages.length : 0;
  const fp = fingerprintMessages(localMessages);

  switch (result.reason) {
    case "count":
      return `Database message count mismatch (${fp} local vs ${dbCount} in DB, phase=${phase}).`;
    case "id":
      return `Database message ids do not match local copy (${fp} local vs ${dbCount} in DB, phase=${phase}).`;
    case "phase":
      return `Messages saved but turn incomplete for phase=${phase} (${fp} local vs ${dbCount} in DB).`;
    case "not_array":
      return `Database messages missing or invalid (${fp} local vs ${dbCount} in DB, phase=${phase}).`;
    default:
      return `Database messages do not match local copy (${fp} local vs ${dbCount} in DB, phase=${phase}).`;
  }
}
